import { urls } from "../urls";

// One view = one session, measured with wall-clock timestamps rather than by
// counting setInterval ticks (which browsers throttle in background tabs).
//
// Delivery is at-least-once-ish and deliberately conservative. The backend
// cannot deduplicate, so a retry after an ambiguous failure would silently
// inflate the numbers. Under-reporting is a bias you can reason about;
// duplicates corrupt the data invisibly. So: retry when the request never got a
// response, drop when the server answered with an error.

const OPEN_SESSION_KEY = "timeTracking.openSession";
const QUEUE_KEY = "timeTracking.queue";
const CHECKPOINT_MS = 30000;

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // A full or unavailable localStorage must never break the app.
  }
};

// "/games/high-low" -> "high-low", "/" -> "home".
//
// The last segment, which reproduces the values the backend already stores, so
// existing reports keep grouping. The old code reached the same answer for deep
// routes via split('/')[4] and for shallow ones via a fallback to [3]; this is
// the same result derived one way, and without the query string, the dead
// resource-list special cases, or the crash on a short URL.
//
// Verified unambiguous: no two of the 54 routes share a last segment.
const moduleNameFor = (pathname) => {
  const trimmed = String(pathname || "").replace(/^\/+|\/+$/g, "");
  if (trimmed === "") {
    return "home";
  }
  const segments = trimmed.split("/");
  return segments[segments.length - 1];
};

const secondsBetween = (startedAt, endedAt) =>
  Math.round((endedAt - startedAt) / 1000);

const enqueue = (event) => {
  const queue = readJSON(QUEUE_KEY, []);
  queue.push(event);
  writeJSON(QUEUE_KEY, queue);
};

const startSession = (module, now = Date.now()) => {
  writeJSON(OPEN_SESSION_KEY, {
    module,
    startedAt: now,
    lastSeenAt: now,
    clicks: 0,
  });
};

// Written every CHECKPOINT_MS so an abandoned session has a known-good end time
// if the tab dies. Local only - no network.
const checkpoint = (clicks, now = Date.now()) => {
  const open = readJSON(OPEN_SESSION_KEY, null);
  if (!open) {
    return;
  }
  writeJSON(OPEN_SESSION_KEY, { ...open, lastSeenAt: now, clicks });
};

const closeSession = (clicks, endedAt) => {
  const open = readJSON(OPEN_SESSION_KEY, null);
  localStorage.removeItem(OPEN_SESSION_KEY);
  if (!open) {
    return null;
  }
  const time = secondsBetween(open.startedAt, endedAt);
  if (time <= 0) {
    return null;
  }
  const event = {
    module: open.module,
    time: time,
    click_engagement: clicks,
  };
  enqueue(event);
  return event;
};

const endSession = (clicks, now = Date.now()) => closeSession(clicks, now);

// On startup, a session left open means the previous one ended without a
// navigation - the tab was closed, the tablet slept, the browser was killed.
// Close it at its last checkpoint, which never over-reports.
const recoverAbandonedSession = () => {
  const open = readJSON(OPEN_SESSION_KEY, null);
  if (!open) {
    return null;
  }
  return closeSession(open.clicks || 0, open.lastSeenAt);
};

const sendEvent = async (event, keepalive) => {
  const token = JSON.parse(localStorage.getItem("token"));
  const response = await fetch(urls.time, {
    method: "POST",
    keepalive: keepalive,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(event),
  });
  return response.ok;
};

let flushing = false;

// Sequential, and stops at the first request that never reached the server so
// ordering is preserved and a dead network is not hammered.
const flushQueue = async ({ keepalive = false } = {}) => {
  if (flushing) {
    return;
  }
  flushing = true;
  try {
    let queue = readJSON(QUEUE_KEY, []);
    while (queue.length > 0) {
      const event = queue[0];
      let delivered;
      try {
        delivered = await sendEvent(event, keepalive);
      } catch (error) {
        // No response at all. Keep it and try again next time.
        break;
      }
      // The server answered. Whether it stored the event or rejected it, we
      // must not send it twice.
      queue = readJSON(QUEUE_KEY, []).slice(1);
      writeJSON(QUEUE_KEY, queue);
      if (!delivered) {
        continue;
      }
    }
  } finally {
    flushing = false;
  }
};

export {
  CHECKPOINT_MS,
  OPEN_SESSION_KEY,
  QUEUE_KEY,
  moduleNameFor,
  startSession,
  checkpoint,
  endSession,
  recoverAbandonedSession,
  flushQueue,
};
